import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
  refreshKey?: number;
};

export type RealClosingRow = {
  real_closing: number | null;
  real_closing_ts: string | null;
  currency: string | null;
};

export type RealClosingMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, RealClosingRow>>;
};

type ManualBalanceRow = {
  id: string;
  bank_account_id: string;
  balance_date: string;
  closing_balance: number | string | null;
  currency: string | null;
  created_at: string | null;
};

const toNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

function buildDateKeys(dateFrom: string, dateTo: string): string[] {
  const start = new Date(`${dateFrom}T00:00:00Z`);
  const end = new Date(`${dateTo}T00:00:00Z`);
  const keys: string[] = [];

  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    keys.push(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`);
  }

  return keys;
}

function rangeTs(dateFrom: string, dateTo: string) {
  const from = `${dateFrom}T00:00:00+00:00`;

  const toPlus1 = new Date(`${dateTo}T00:00:00Z`);
  toPlus1.setUTCDate(toPlus1.getUTCDate() + 1);
  const to = toPlus1.toISOString();

  return { from, to };
}

function compareIsoTs(a: string | null | undefined, b: string | null | undefined) {
  const aa = a ?? "";
  const bb = b ?? "";
  if (aa < bb) return -1;
  if (aa > bb) return 1;
  return 0;
}

/**
 * Pick the latest day with a non-null real_closing whose dayKey is <= asOfDate.
 */
export function pickEffectiveClosingAsOf(
  dayMap: Map<string, RealClosingRow> | undefined,
  asOfDate: string
): { dayKey: string; row: RealClosingRow } | null {
  if (!dayMap || dayMap.size === 0) return null;

  const entries = Array.from(dayMap.entries())
    .filter(([dayKey, row]) => row.real_closing != null && dayKey <= asOfDate)
    .sort(([a], [b]) => a.localeCompare(b));

  if (!entries.length) return null;

  const [dayKey, row] = entries[entries.length - 1];
  return { dayKey, row };
}

export function useRealClosingMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo, refreshKey = 0 } = args;

  return useQuery({
    queryKey: ["real_closing_matrix_rpc", [...bankAccountIds].sort(), dateFrom, dateTo, refreshKey],
    enabled: bankAccountIds.length > 0 && !!dateFrom && !!dateTo,
    queryFn: async (): Promise<RealClosingMatrix> => {
      const dateKeys = buildDateKeys(dateFrom, dateTo);
      const { from: tsFrom, to: tsToPlus1 } = rangeTs(dateFrom, dateTo);

      const [txRes, manualRes] = await Promise.all([
        (supabase as any).rpc("get_bank_tx_daily_first_last", {
          p_account_ids: bankAccountIds,
          p_from: tsFrom,
          p_to: tsToPlus1,
        }),
        supabase
          .from("bank_account_balances")
          .select("id, bank_account_id, balance_date, closing_balance, currency, created_at")
          .in("bank_account_id", bankAccountIds)
          .gte("balance_date", dateFrom)
          .lte("balance_date", dateTo)
          .not("closing_balance", "is", null)
          .order("bank_account_id", { ascending: true })
          .order("balance_date", { ascending: true })
          .order("created_at", { ascending: true })
          .limit(10000),
      ]);

      if (txRes.error) throw txRes.error;
      if (manualRes.error) throw manualRes.error;

      const txData = (txRes.data ?? []) as any[];
      const manualData = (manualRes.data ?? []) as ManualBalanceRow[];

      const byAccount = new Map<string, Map<string, RealClosingRow>>();
      for (const id of bankAccountIds) {
        byAccount.set(id, new Map());
      }

      for (const tx of txData) {
        const accId = String(tx.bank_account_id);
        const dayKey = String(tx.day).slice(0, 10);

        const accMap = byAccount.get(accId) ?? new Map<string, RealClosingRow>();
        accMap.set(dayKey, {
          real_closing: toNum(tx.last_end_balance),
          real_closing_ts: tx.last_ts ?? null,
          currency: tx.last_end_balance_currency ?? tx.last_transaction_amount_currency ?? null,
        });
        byAccount.set(accId, accMap);
      }

      // Manual latest per (account, day) wins over tx closing
      const manualLatestByDay = new Map<string, Map<string, ManualBalanceRow>>();

      for (const row of manualData) {
        const accId = String(row.bank_account_id);
        const dayKey = String(row.balance_date).slice(0, 10);

        if (!manualLatestByDay.has(accId)) {
          manualLatestByDay.set(accId, new Map());
        }
        const dayMap = manualLatestByDay.get(accId)!;
        const existing = dayMap.get(dayKey);

        if (!existing || compareIsoTs(existing.created_at, row.created_at) < 0) {
          dayMap.set(dayKey, row);
        }
      }

      for (const accId of bankAccountIds) {
        const accMap = byAccount.get(accId) ?? new Map<string, RealClosingRow>();
        const manualMap = manualLatestByDay.get(accId);
        if (!manualMap) {
          byAccount.set(accId, accMap);
          continue;
        }
        for (const [dayKey, row] of manualMap.entries()) {
          accMap.set(dayKey, {
            real_closing: toNum(row.closing_balance),
            real_closing_ts: row.created_at ?? null,
            currency: row.currency ?? null,
          });
        }
        byAccount.set(accId, accMap);
      }

      return { dateKeys, byAccount };
    },
    staleTime: 15_000,
  });
}
